// WIETZ – Wiener Tierseuchengesetz / Gesundheitsüberwachung
// Gesundheitsüberwachung gem. §§ 49–52 ASchG iVm AMR 4 (Biologische Arbeitsstoffe)
// Tätigkeitsbezogene Vorsorgeuntersuchungen für Beschäftigte mit Exposition
// gegenüber biologischen Arbeitsstoffen der Gruppen 2–4 (EU-Richtlinie 2000/54/EG)

export type WietzKategorie =
  | 'Tierhaltung'
  | 'Labor'
  | 'Gesundheit'
  | 'Abfall'
  | 'Landwirtschaft'
  | 'Sonstige';

export interface WietzTatbestand {
  code:          string;           // z.B. "WT1"
  bezeichnung:   string;
  kategorie:     WietzKategorie;
  erreger:       string[];         // relevante biologische Arbeitsstoffe
  intervallMon:  12 | 24 | 36 | 60;
  pflichtDoku:   string[];
  gesetzGrundl:  string;
  aktiv:         boolean;
}

export const WIETZ_TATBESTAENDE: readonly WietzTatbestand[] = [
  // ── Tierhaltung ───────────────────────────────────────────────────────────
  {
    code: 'WT1', bezeichnung: 'Tierpfleger/innen in Tierhaltungsanlagen',
    kategorie: 'Tierhaltung',
    erreger: ['Coxiella burnetii (Q-Fieber)', 'Brucella spp.', 'Leptospira spp.'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Befund', 'Laborwerte'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  {
    code: 'WT2', bezeichnung: 'Schlachthofpersonal',
    kategorie: 'Tierhaltung',
    erreger: ['Salmonella spp.', 'Campylobacter spp.', 'Erysipelothrix rhusiopathiae'],
    intervallMon: 12, pflichtDoku: ['Anamnese', 'Haut-/Schleimhautbefund', 'Stuhluntersuchung bei Indikation'],
    gesetzGrundl: '§49 ASchG, AMR 4, VO (EG) 852/2004', aktiv: true,
  },
  {
    code: 'WT3', bezeichnung: 'Tierärzte und tierärztliches Assistenzpersonal',
    kategorie: 'Tierhaltung',
    erreger: ['Brucella spp.', 'Toxoplasma gondii', 'Tollwutvirus'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Impfstatus Tollwut', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  {
    code: 'WT4', bezeichnung: 'Beschäftigte in der Geflügelhaltung',
    kategorie: 'Tierhaltung',
    erreger: ['Influenzavirus A (aviär)', 'Chlamydophila psittaci', 'Salmonella spp.'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Lungenfunktion', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  // ── Labor ──────────────────────────────────────────────────────────────────
  {
    code: 'WL1', bezeichnung: 'Laborpersonal mit Exposition gg. Gruppe-2-Arbeitsstoffe',
    kategorie: 'Labor',
    erreger: ['Hepatitis B', 'Hepatitis C', 'HIV (Nadelstichverletzung)'],
    intervallMon: 36, pflichtDoku: ['Impfstatus HBV', 'Anti-HBs-Titer', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4, BiostoffV', aktiv: true,
  },
  {
    code: 'WL2', bezeichnung: 'Laborpersonal mit Exposition gg. Gruppe-3-Arbeitsstoffe',
    kategorie: 'Labor',
    erreger: ['Mycobacterium tuberculosis', 'Brucella spp.', 'Hepatitis B/C'],
    intervallMon: 12, pflichtDoku: ['Anamnese', 'Tuberkulin-/IGRA-Test', 'Impfstatus', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  // ── Gesundheitswesen ───────────────────────────────────────────────────────
  {
    code: 'WG1', bezeichnung: 'Pflegepersonal mit direktem Patientenkontakt',
    kategorie: 'Gesundheit',
    erreger: ['Hepatitis B', 'Influenzavirus', 'MRSA'],
    intervallMon: 36, pflichtDoku: ['Impfstatus', 'Anti-HBs-Titer', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  {
    code: 'WG2', bezeichnung: 'Rettungsdienst / Ersthelfer',
    kategorie: 'Gesundheit',
    erreger: ['Hepatitis B', 'HIV', 'SARS-CoV-2'],
    intervallMon: 36, pflichtDoku: ['Impfstatus HBV/Tetanus', 'Anti-HBs-Titer', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  // ── Abfall / Entsorgung ────────────────────────────────────────────────────
  {
    code: 'WA1', bezeichnung: 'Müllwerker / Abfallsammlung',
    kategorie: 'Abfall',
    erreger: ['Leptospira spp.', 'Hantavirus', 'Hepatitis A'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Impfstatus HepA/Tetanus', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  {
    code: 'WA2', bezeichnung: 'Kläranlagenpersonal',
    kategorie: 'Abfall',
    erreger: ['Leptospira spp.', 'E. coli O157:H7', 'Hepatitis A'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Impfstatus', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  // ── Landwirtschaft ─────────────────────────────────────────────────────────
  {
    code: 'WF1', bezeichnung: 'Forstarbeiter (Zeckenexposition)',
    kategorie: 'Landwirtschaft',
    erreger: ['Borrelia burgdorferi', 'FSME-Virus', 'Anaplasma phagocytophilum'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Impfstatus FSME', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  {
    code: 'WF2', bezeichnung: 'Landwirtschaftliche Beschäftigte (Pilzsporen)',
    kategorie: 'Landwirtschaft',
    erreger: ['Aspergillus spp.', 'Actinomyceten', 'Thermophile Aktinomyzeten'],
    intervallMon: 24, pflichtDoku: ['Anamnese', 'Lungenfunktion', 'Röntgen Thorax bei Indikation'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
  // ── Sonstige ──────────────────────────────────────────────────────────────
  {
    code: 'WS1', bezeichnung: 'Reinigungspersonal in Krankenhäusern',
    kategorie: 'Sonstige',
    erreger: ['Hepatitis B', 'Noro-/Rotavirus', 'MRSA'],
    intervallMon: 36, pflichtDoku: ['Impfstatus', 'Anti-HBs-Titer', 'Befund'],
    gesetzGrundl: '§49 ASchG, AMR 4', aktiv: true,
  },
] as const;

export const WIETZ_MAP = new Map<string, WietzTatbestand>(
  WIETZ_TATBESTAENDE.map(t => [t.code, t])
);

export const WIETZ_KATEGORIEN: WietzKategorie[] = [
  'Tierhaltung', 'Labor', 'Gesundheit', 'Abfall', 'Landwirtschaft', 'Sonstige',
];
